import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {
  IWidgetControllerConfig,
  SiteStructure,
} from '@wix/native-components-infra/dist/src/types/types'
import {BreadcrumbsItem} from 'wix-ui-tpa/dist/cssVars/types/components'

import {noop} from '../../utils/helpers'
import {AppPage, EXPERIENCES_LIST_REFERRER} from '../../utils/constants'
import {goToExperienceDetails} from '../../utils/navigation'
import {createStorageProvider} from '../../utils/storageContext'
import {RequestStatus, wrapRequest} from '../../utils/wrapRequest'
import {experiencesService} from '../../services/experiencesService'
import {getBreadcrumbsConfig} from '../../utils/getBreadcrumbsConfig'
import {reservationLocationsService} from '../../services/reservationLocationsService'
import {getRegionalSettings} from '../../utils/flowAPI'
import {getReservationLocationsMock} from '../../editor/editorMocks/getReservationLocationsMock'
import {getExperiencesMock} from '../../editor/editorMocks/getExperiencesMock'
import {DEFAULT_FILTER_CONFIG, ExperiencesFilterConfig} from '../../utils/experiencesFilterConfig'

interface ExperiencesListData {
  getReservationLocationsStatus: RequestStatus
  getExperiencesStatus: RequestStatus
  breadcrumbsConfig: BreadcrumbsItem[]
  regionalSettings?: string
  reservationLocations: ReservationLocation[]
  experiences: Experience[]
  fitToContentHeight: boolean
  filterConfig: ExperiencesFilterConfig
  isOrderingEnabled: boolean
}

interface ExperiencesListActions {
  getReservationLocations: () => Promise<ReservationLocation[] | undefined>
  getExperiences: () => Promise<Experience[] | undefined>
  goToExperienceDetailsPage: (experience: Experience) => void
}

export interface ExperiencesListStorage extends ExperiencesListData, ExperiencesListActions {}

const defaultExperiencesListStorage: ExperiencesListStorage = {
  getReservationLocationsStatus: RequestStatus.DEFAULT,
  getExperiencesStatus: RequestStatus.DEFAULT,
  breadcrumbsConfig: [],
  reservationLocations: [],
  experiences: [],
  fitToContentHeight: true,
  filterConfig: DEFAULT_FILTER_CONFIG,
  isOrderingEnabled: false,
  getReservationLocations: noop,
  getExperiences: noop,
  goToExperienceDetailsPage: noop,
}

const {withStorageProvider, useStorage} = createStorageProvider(defaultExperiencesListStorage)

const initExperiencesListStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
  siteStructure: SiteStructure,
  filterConfig: ExperiencesFilterConfig,
  isOrderingEnabled: boolean,
): ExperiencesListStorage => {
  return {
    ...defaultExperiencesListStorage,
    filterConfig,
    isOrderingEnabled,
    breadcrumbsConfig: getBreadcrumbsConfig(flowAPI, siteStructure, AppPage.ExperiencesList),
    regionalSettings: getRegionalSettings(flowAPI),
    getReservationLocations: wrapRequest(
      flowAPI,
      reservationLocationsService.getReservationLocations,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getExperiences: wrapRequest(
      flowAPI,
      experiencesService.getExperiences,
      'experiences',
      'getExperiencesStatus',
    ),
    goToExperienceDetailsPage: (experience: Experience) =>
      goToExperienceDetails({flowAPI, experience, referrer: EXPERIENCES_LIST_REFERRER}),
  }
}

const mockExperiencesListStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
  siteStructure: SiteStructure,
  filterConfig: ExperiencesFilterConfig,
  isOrderingEnabled: boolean,
): ExperiencesListStorage => {
  const initiatedStorage = initExperiencesListStorage(
    flowAPI,
    controllerConfig,
    siteStructure,
    filterConfig,
    isOrderingEnabled,
  )

  return {
    ...initiatedStorage,
    getReservationLocations: wrapRequest(
      flowAPI,
      getReservationLocationsMock,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getExperiences: wrapRequest(flowAPI, getExperiencesMock, 'experiences', 'getExperiencesStatus'),
  }
}

export {
  initExperiencesListStorage,
  mockExperiencesListStorage,
  withStorageProvider as withExperiencesListStorageProvider,
  useStorage as useExperiencesListStorage,
}
